'use client';

import { createContext, ReactNode, useContext } from 'react';

// Define the permission type based on your JSON data
export type PermissionKey =
  | 'real_time_monitoring'
  | 'customer_profiling'
  | 'system_monitoring'
  | 'case_management'
  | 'basic_reporting'
  | 'api_integration'
  | 'basic_compliance'
  | 'core_security'
  | 'natural_language_ai_assistant'
  | 'customer_relationship_mapping'
  | 'advanced_compliance_screening'
  | 'advanced_analytics'
  | 'white_label_branding'
  | 'custom_development_support'
  | 'zero_trust_security'
  | 'no_code_rule_builder'
  | 'multi_condition_rules'
  | 'rule_performance_analytics'
  | 'version_control'
  | 'natural_language_rule_creation'
  | 'ai_rule_suggestions';

interface PermissionContextType {
  permissions: PermissionKey[];
  hasPermission: (permission: PermissionKey | PermissionKey[]) => boolean;
  hasAnyPermission: (permissions: PermissionKey[]) => boolean;
  hasAllPermissions: (permissions: PermissionKey[]) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined
);

interface PermissionProviderProps {
  children: ReactNode;
  permissions: PermissionKey[];
}

export function PermissionProvider({
  children,
  permissions,
}: PermissionProviderProps) {
  const hasPermission = (
    permission: PermissionKey | PermissionKey[]
  ): boolean => {
    if (Array.isArray(permission)) {
      return permission.every((p) => permissions.includes(p));
    }
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: PermissionKey[]): boolean => {
    return permissionList.some((permission) =>
      permissions.includes(permission)
    );
  };

  const hasAllPermissions = (permissionList: PermissionKey[]): boolean => {
    return permissionList.every((permission) =>
      permissions.includes(permission)
    );
  };

  const value: PermissionContextType = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}
